import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Modal that prompts the player to make per-cast choices (e.g. Resistance's
 * damage type) before targeting begins. Emits `spell-choices-submit` with a
 * resolved-values map on confirm, and `spell-choices-cancel` on dismiss.
 *
 * The `choices` attribute is the spell's declared choices array; each entry
 * is `{id, name, options, description}`. One radio group per choice; the
 * submit button enables once every group has a selection.
 */
export class SpellChoicesModal extends BaseComponent {
  static properties = {
    spellName: { type: String, attribute: 'spell-name' },
    choices: { type: Array },
    _selected: { state: true },
  };

  static styles = css`
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    .modal-menu {
      background: var(--bs-body-bg, #1f1f1f);
      color: var(--bs-body-color, #fff);
      border: 1px solid var(--bs-border-color, #444);
      border-radius: 8px;
      min-width: min(440px, 95vw);
      max-width: min(560px, 95vw);
      max-height: 90vh;
      overflow: auto;
      display: flex;
      flex-direction: column;
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid var(--bs-border-color, #444);
    }
    .modal-header h3 {
      margin: 0;
      font-size: 1.1rem;
    }
    .modal-close {
      background: transparent;
      border: 0;
      color: inherit;
      font-size: 1.5rem;
      cursor: pointer;
    }
    .modal-content {
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .choice-block {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .choice-block-title {
      font-weight: 600;
    }
    .choice-block-description {
      font-size: 0.85em;
      opacity: 0.8;
    }
    .choice-options {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 6px;
    }
    .choice-option {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 8px;
      border: 1px solid var(--bs-border-color, #444);
      border-radius: 4px;
      cursor: pointer;
    }
    .choice-option input {
      margin: 0;
    }
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 12px 16px;
      border-top: 1px solid var(--bs-border-color, #444);
    }
  `;

  constructor() {
    super();
    this.spellName = '';
    /** @type {Array<{id:string,name?:string,options?:string[],description?:string}>} */
    this.choices = [];
    /** @type {Record<string,string>} */
    this._selected = {};
  }

  /** @param {string} choiceId @param {string} option */
  _pick(choiceId, option) {
    this._selected = { ...this._selected, [choiceId]: option };
  }

  _isComplete() {
    for (const c of this.choices || []) {
      if (!this._selected[c.id]) return false;
    }
    return true;
  }

  _cancel() {
    this.dispatchEvent(new CustomEvent('spell-choices-cancel', { bubbles: true, composed: true }));
  }

  _submit() {
    if (!this._isComplete()) return;
    this.dispatchEvent(
      new CustomEvent('spell-choices-submit', {
        detail: { choices: { ...this._selected } },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div class="modal-backdrop" @click="${() => this._cancel()}">
        <div
          class="modal-menu"
          @click="${/** @param {Event} e */ (e) => e.stopPropagation()}"
        >
          <div class="modal-header">
            <h3>${this.spellName || 'Spell'} — choose</h3>
            <button class="modal-close" @click="${() => this._cancel()}">&times;</button>
          </div>
          <div class="modal-content">
            ${(this.choices || []).map(
              (choice) => html`
                <div class="choice-block">
                  <div class="choice-block-title">${choice.name || choice.id}</div>
                  ${choice.description ? html`<div class="choice-block-description">${choice.description}</div>` : ''}
                  <div class="choice-options">
                    ${(choice.options || []).map(
                      (opt) => html`
                        <label class="choice-option">
                          <input
                            type="radio"
                            name="${choice.id}"
                            value="${opt}"
                            .checked="${this._selected[choice.id] === opt}"
                            @change="${() => this._pick(choice.id, opt)}"
                          />
                          ${opt}
                        </label>
                      `,
                    )}
                  </div>
                </div>
              `,
            )}
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="${() => this._cancel()}">Cancel</button>
            <button
              class="btn btn-primary"
              ?disabled="${!this._isComplete()}"
              @click="${() => this._submit()}"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('spell-choices-modal', SpellChoicesModal);
